import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom"; // 🆕 NUEVO
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  Refresh,
  Cancel,
  CheckCircle,
  Warning,
  TrendingUp,
  AttachMoney,
  Pending,
  FilterList,
  Info, // 🆕 NUEVO: Ícono para notificaciones de tipo info
  History, // 🆕 NUEVO: Ícono para historial de auditoría
  Edit, // 🆕 NUEVO: Ícono para editar venta
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useAuth } from "../../../context/context";
import directSalesService from "../../../services/directSalesService";
import DirectSaleModal from "../../../components/forms/DirectSaleModal";

export const DirectSalesList = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams(); // 🆕 NUEVO
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    saleId: null,
    saleName: "",
  });
  const [cancellationReason, setCancellationReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  // 🆕 NUEVO: Estado para el modal de reactivación
  const [reactivateDialog, setReactivateDialog] = useState({
    open: false,
    saleId: null,
    saleName: "",
  });
  const [reactivating, setReactivating] = useState(false);

  // 🆕 NUEVO: Estado para modal de notificación
  const [notificationDialog, setNotificationDialog] = useState({
    open: false,
    title: "",
    message: "",
    severity: "success", // success, error, warning, info
  });

  // 🆕 NUEVO: Estado para el historial de auditoría
  const [auditHistoryDialog, setAuditHistoryDialog] = useState({
    open: false,
    saleId: null,
    saleName: "",
  });
  const [auditHistory, setAuditHistory] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);

  // 🆕 NUEVO: Estado para el modal de edición
  const [editSaleDialog, setEditSaleDialog] = useState({
    open: false,
    sale: null,
  });

  const [filters, setFilters] = useState({
    paymentStatus: "",
    dateFrom: "",
    dateTo: "",
    search: "", // 🆕 NUEVO: Campo de búsqueda
  });
  const [showFilters, setShowFilters] = useState(true);

  const loadSales = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;

      const response = await directSalesService.getDirectSales(params, token);
      const salesData = response.data || [];

      // 🆕 DEBUG: Ver la estructura real de los datos
      console.log("📊 Ventas recibidas del backend:", salesData);
      if (salesData.length > 0) {
        console.log("📋 Ejemplo de venta:", salesData[0]);
        console.log(
          "❓ cancelledAt del primer registro:",
          salesData[0].cancelledAt
        );
        console.log("❓ Tipo de cancelledAt:", typeof salesData[0].cancelledAt);
      }

      setSales(salesData);
    } catch (err) {
      setError(err.message || "Error al cargar ventas directas");
      console.error("Error loading sales:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, token]);

  const loadStats = useCallback(async () => {
    try {
      const params = {};
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;

      const response = await directSalesService.getStats(params, token);
      setStats(response.data);
    } catch (err) {
      console.error("Error loading stats:", err);
    }
  }, [filters, token]);

  useEffect(() => {
    loadSales();
    loadStats();
  }, [loadSales, loadStats]);

  // 🆕 NUEVO: Leer parámetro search de la URL y aplicarlo automáticamente
  useEffect(() => {
    const searchQuery = searchParams.get("search");

    if (searchQuery && searchQuery.trim() !== "") {
      console.log(
        "🔍 DirectSalesList: Aplicando búsqueda desde URL:",
        searchQuery
      );
      setFilters((prev) => ({ ...prev, search: searchQuery }));
    }
  }, [searchParams]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("es-CL", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  };

  const formatPrice = (price) => {
    if (!price) return "N/A";
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleCancelClick = (sale) => {
    setCancelDialog({
      open: true,
      saleId: sale.id,
      saleName: sale.lot?.name || "Sin nombre",
    });
    setCancellationReason("");
  };

  const handleCancelConfirm = async () => {
    if (!cancellationReason.trim()) {
      alert("Debes ingresar un motivo de cancelación");
      return;
    }

    setCancelling(true);
    try {
      await directSalesService.cancelDirectSale(
        cancelDialog.saleId,
        cancellationReason,
        token
      );

      await loadSales();
      await loadStats();

      setCancelDialog({ open: false, saleId: null, saleName: "" });
      setCancellationReason("");

      setNotificationDialog({
        open: true,
        title: "Venta Cancelada",
        message:
          "Venta cancelada exitosamente. El producto vuelve a estar disponible.",
        severity: "success",
      });
    } catch (err) {
      setNotificationDialog({
        open: true,
        title: "Error",
        message: err.message || "Error al cancelar la venta",
        severity: "error",
      });
    } finally {
      setCancelling(false);
    }
  };

  const handlePaymentStatusChange = async (saleId, newStatus) => {
    try {
      await directSalesService.updatePaymentStatus(saleId, newStatus, token);
      await loadSales();
      await loadStats();
    } catch (err) {
      setNotificationDialog({
        open: true,
        title: "Error",
        message: err.message || "Error al actualizar estado de pago",
        severity: "error",
      });
    }
  };

  // 🆕 NUEVO: Función helper para verificar si una venta está cancelada
  const isSaleCancelled = (sale) => {
    if (!sale) return false;

    const cancelledAt = sale.cancelledAt;

    // Verificar si es null, undefined, string vacío, o "null" como string
    if (
      cancelledAt === null ||
      cancelledAt === undefined ||
      cancelledAt === "" ||
      cancelledAt === "null"
    ) {
      return false;
    }

    // Si tiene algún valor válido (fecha), está cancelada
    return true;
  };

  // 🆕 NUEVO: Manejar clic en reactivar
  const handleReactivateClick = (sale) => {
    setReactivateDialog({
      open: true,
      saleId: sale.id,
      saleName: sale.lot?.name || "Sin nombre",
    });
  };

  // 🆕 NUEVO: Confirmar reactivación
  const handleReactivateConfirm = async () => {
    setReactivating(true);
    try {
      await directSalesService.reactivateDirectSale(
        reactivateDialog.saleId,
        token
      );

      await loadSales();
      await loadStats();

      setReactivateDialog({ open: false, saleId: null, saleName: "" });

      setNotificationDialog({
        open: true,
        title: "Venta Reactivada",
        message:
          "Venta reactivada exitosamente. El producto vuelve a estar marcado como vendido.",
        severity: "success",
      });
    } catch (err) {
      setNotificationDialog({
        open: true,
        title: "Error",
        message: err.message || "Error al reactivar la venta",
        severity: "error",
      });
    } finally {
      setReactivating(false);
    }
  };

  // 🆕 NUEVO: Abrir modal de historial de auditoría
  const handleViewAuditHistory = async (sale) => {
    setAuditHistoryDialog({
      open: true,
      saleId: sale.id,
      saleName: sale.lot?.name || "Sin nombre",
    });
    setAuditHistory(null);
    setLoadingAudit(true);

    try {
      const response = await directSalesService.getAuditHistory(sale.id, token);
      setAuditHistory(response.data);
    } catch (err) {
      setNotificationDialog({
        open: true,
        title: "Error",
        message: err.message || "Error al cargar historial de auditoría",
        severity: "error",
      });
      setAuditHistoryDialog({ open: false, saleId: null, saleName: "" });
    } finally {
      setLoadingAudit(false);
    }
  };

  // 🆕 NUEVO: Abrir modal de edición
  const handleEditClick = (sale) => {
    setEditSaleDialog({
      open: true,
      sale: sale,
    });
  };

  // 🆕 NUEVO: Callback cuando se actualiza una venta
  const handleSaleUpdated = async (updatedSale) => {
    await loadSales();
    await loadStats();
    setEditSaleDialog({ open: false, sale: null });
    setNotificationDialog({
      open: true,
      title: "Venta Actualizada",
      message: "La venta ha sido actualizada exitosamente.",
      severity: "success",
    });
  };

  // 🆕 NUEVO: Filtrar ventas en el frontend basado en el término de búsqueda
  const filteredSales = React.useMemo(() => {
    if (!filters.search || filters.search.trim() === "") {
      return sales;
    }

    const searchTerm = filters.search.toLowerCase().trim();

    return sales.filter((sale) => {
      // Buscar por ID de venta
      if (sale.id?.toString().includes(searchTerm)) return true;

      // Buscar por nombre del producto
      if (sale.lot?.name?.toLowerCase().includes(searchTerm)) return true;

      // Buscar por email del comprador
      if (sale.buyer?.email?.toLowerCase().includes(searchTerm)) return true;

      // Buscar por método de pago
      if (sale.paymentMethod?.toLowerCase().includes(searchTerm)) return true;

      // Buscar por precio final
      if (sale.finalPrice?.toString().includes(searchTerm)) return true;

      return false;
    });
  }, [sales, filters.search]);

  const clearFilters = () => {
    setFilters({
      paymentStatus: "",
      dateFrom: "",
      dateTo: "",
      search: "",
    });
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 80,
    },
    {
      field: "lot",
      headerName: "Producto",
      flex: 2,
      minWidth: 250,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Typography variant="body2" fontWeight="medium">
            {params.value?.name || "Sin nombre"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Precio lista: {formatPrice(params.value?.price)}
          </Typography>
        </Box>
      ),
    },
    {
      field: "buyer",
      headerName: "Comprador",
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Typography variant="body2">
            {params.value?.email || "Sin email"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ID: {params.value?.id}
          </Typography>
        </Box>
      ),
    },
    {
      field: "finalPrice",
      headerName: "Precio Final",
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {formatPrice(params.value)}
        </Typography>
      ),
    },
    {
      field: "paymentStatus",
      headerName: "Estado Pago",
      width: 150,
      renderCell: (params) => {
        const isCancelled = isSaleCancelled(params.row);

        // Si está cancelada, no permitir editar el estado de pago
        if (isCancelled) {
          return <Chip label="N/A" color="default" size="small" disabled />;
        }

        // Mostrar dropdown editable con el estado de pago actual
        return (
          <FormControl size="small" fullWidth>
            <Select
              value={params.value || "pending"}
              onChange={(e) =>
                handlePaymentStatusChange(params.row.id, e.target.value)
              }
              size="small"
              MenuProps={{ disableScrollLock: true }}
              sx={{ fontSize: "0.875rem" }}
            >
              <MenuItem value="pending">
                <Chip label="Pendiente" color="warning" size="small" />
              </MenuItem>
              <MenuItem value="partial">
                <Chip label="Parcial" color="info" size="small" />
              </MenuItem>
              <MenuItem value="completed">
                <Chip label="Completado" color="success" size="small" />
              </MenuItem>
            </Select>
          </FormControl>
        );
      },
    },
    {
      field: "paymentMethod",
      headerName: "Método Pago",
      width: 150,
      renderCell: (params) => (
        <Chip label={params.value || "N/A"} size="small" variant="outlined" />
      ),
    },
    {
      field: "soldAt",
      headerName: "Fecha Venta",
      width: 160,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "cancelledAt",
      headerName: "Estado",
      width: 130,
      renderCell: (params) => {
        const isCancelled = isSaleCancelled(params.row);
        return isCancelled ? (
          <Chip
            label="CANCELADA"
            color="error"
            size="small"
            icon={<Cancel />}
          />
        ) : (
          <Chip
            label="ACTIVA"
            color="success"
            size="small"
            icon={<CheckCircle />}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 140,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const isCancelled = isSaleCancelled(params.row);
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Ver historial">
              <IconButton
                size="small"
                color="info"
                onClick={() => handleViewAuditHistory(params.row)}
              >
                <History />
              </IconButton>
            </Tooltip>
            {!isCancelled && (
              <Tooltip title="Cancelar venta">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleCancelClick(params.row)}
                >
                  <Cancel />
                </IconButton>
              </Tooltip>
            )}
            {isCancelled && (
              <Tooltip title="Reactivar venta">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleReactivateClick(params.row)}
                >
                  <CheckCircle />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Editar venta">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleEditClick(params.row)}
              >
                <Edit />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Ventas Directas
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            variant="outlined"
            size="small"
          >
            {showFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
          </Button>
          <Button
            startIcon={<Refresh />}
            onClick={() => {
              loadSales();
              loadStats();
            }}
            disabled={loading}
            variant="outlined"
            size="small"
          >
            Actualizar
          </Button>
        </Box>
      </Box>

      {showFilters && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Filtros
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              {/* 🆕 NUEVO: Buscador */}
              <TextField
                fullWidth
                size="small"
                placeholder="🔍 Buscar por ID, producto, comprador, método de pago o precio..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                helperText={
                  filters.search
                    ? `${filteredSales.length} resultado(s) encontrado(s)`
                    : "Escribe para buscar en todas las ventas"
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado de Pago</InputLabel>
                <Select
                  value={filters.paymentStatus}
                  label="Estado de Pago"
                  MenuProps={{ disableScrollLock: true }}
                  onChange={(e) =>
                    setFilters({ ...filters, paymentStatus: e.target.value })
                  }
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="pending">Pendiente</MenuItem>
                  <MenuItem value="partial">Parcial</MenuItem>
                  <MenuItem value="completed">Completado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Fecha Desde"
                type="date"
                value={filters.dateFrom}
                onChange={(e) =>
                  setFilters({ ...filters, dateFrom: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Fecha Hasta"
                type="date"
                value={filters.dateTo}
                onChange={(e) =>
                  setFilters({ ...filters, dateTo: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={clearFilters}
                size="small"
              >
                Limpiar Filtros
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={filteredSales}
          columns={columns}
          loading={loading}
          disableSelectionOnClick
          localeText={{
            noRowsLabel: "No hay ventas directas registradas",
          }}
          getRowClassName={(params) => {
            if (isSaleCancelled(params.row)) return "row-cancelled";
            return "";
          }}
          initialState={{ pinnedColumns: { right: ["actions"] } }}
          sx={{
            "& .row-cancelled": {
              backgroundColor: "rgba(211, 47, 47, 0.05)",
              opacity: 0.7,
            },
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      </Paper>

      <Dialog
        open={cancelDialog.open}
        disableScrollLock
        onClose={() =>
          !cancelling &&
          setCancelDialog({ open: false, saleId: null, saleName: "" })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Warning color="warning" />
            <Typography variant="h6">Cancelar Venta Directa</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            ¿Estás seguro de que deseas cancelar esta venta? El producto volverá
            a estar disponible en el catálogo.
          </Alert>

          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Producto:</strong> {cancelDialog.saleName}
          </Typography>

          <TextField
            fullWidth
            label="Motivo de cancelación *"
            multiline
            rows={4}
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            placeholder="Ej: Error al registrar, el comprador se arrepintió..."
            disabled={cancelling}
            helperText="Este motivo quedará registrado en el sistema"
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setCancelDialog({ open: false, saleId: null, saleName: "" })
            }
            disabled={cancelling}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCancelConfirm}
            disabled={cancelling || !cancellationReason.trim()}
            variant="contained"
            color="error"
            startIcon={cancelling ? <CircularProgress size={20} /> : <Cancel />}
          >
            {cancelling ? "Cancelando..." : "Confirmar Cancelación"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reactivateDialog.open}
        onClose={() =>
          !reactivating &&
          setReactivateDialog({ open: false, saleId: null, saleName: "" })
        }
        maxWidth="sm"
        disableScrollLock
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircle color="success" />
            <Typography variant="h6">Reactivar Venta Directa</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            ¿Estás seguro de que deseas reactivar esta venta? El producto
            volverá a estar marcado como vendido.
          </Alert>

          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Producto:</strong> {reactivateDialog.saleName}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setReactivateDialog({ open: false, saleId: null, saleName: "" })
            }
            disabled={reactivating}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleReactivateConfirm}
            disabled={reactivating}
            variant="contained"
            color="success"
            startIcon={
              reactivating ? <CircularProgress size={20} /> : <CheckCircle />
            }
          >
            {reactivating ? "Reactivando..." : "Confirmar Reactivación"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🆕 NUEVO: Modal de notificación */}
      <Dialog
        open={notificationDialog.open}
        onClose={() =>
          setNotificationDialog({
            open: false,
            title: "",
            message: "",
            severity: "success",
          })
        }
        maxWidth="sm"
        disableScrollLock
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {notificationDialog.severity === "success" && (
              <CheckCircle color="success" />
            )}
            {notificationDialog.severity === "error" && (
              <Cancel color="error" />
            )}
            {notificationDialog.severity === "warning" && (
              <Warning color="warning" />
            )}
            {notificationDialog.severity === "info" && <Info color="info" />}
            <Typography variant="h6">{notificationDialog.title}</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2">{notificationDialog.message}</Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setNotificationDialog({
                open: false,
                title: "",
                message: "",
                severity: "success",
              })
            }
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🆕 NUEVO: Modal de Historial de Auditoría */}
      <Dialog
        open={auditHistoryDialog.open}
        onClose={() =>
          setAuditHistoryDialog({ open: false, saleId: null, saleName: "" })
        }
        maxWidth="md"
        disableScrollLock
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <History color="info" />
            <Box>
              <Typography variant="h6">Historial de Auditoría</Typography>
              <Typography variant="caption" color="text.secondary">
                {auditHistoryDialog.saleName}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {loadingAudit && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loadingAudit && auditHistory && (
            <Box>
              {/* Información actual */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: "info.lighter" }}>
                <Typography variant="subtitle2" gutterBottom>
                  Estado Actual
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Chip
                    label={`ID: ${auditHistory.saleId}`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Lote: ${auditHistory.lotId}`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={
                      auditHistory.currentStatus.isCancelled
                        ? "CANCELADA"
                        : "ACTIVA"
                    }
                    size="small"
                    color={
                      auditHistory.currentStatus.isCancelled
                        ? "error"
                        : "success"
                    }
                  />
                  <Chip
                    label={`Pago: ${
                      auditHistory.currentStatus.paymentStatus || "N/A"
                    }`}
                    size="small"
                    color="info"
                  />
                </Box>
              </Paper>

              {/* Timeline de eventos */}
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Historial de Cambios ({auditHistory.timeline.length} eventos)
              </Typography>

              <Box sx={{ position: "relative", pl: 3 }}>
                {/* Línea vertical del timeline */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 11,
                    top: 8,
                    bottom: 8,
                    width: 2,
                    bgcolor: "divider",
                  }}
                />

                {/* Eventos - Ordenados de más reciente a más antiguo */}
                {[...auditHistory.timeline].reverse().map((event, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: "relative",
                      mb: 3,
                      "&:last-child": { mb: 0 },
                    }}
                  >
                    {/* Punto del timeline */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: -24,
                        top: 4,
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        bgcolor: "background.paper",
                        border: 2,
                        borderColor:
                          event.action === "created"
                            ? "success.main"
                            : event.action === "cancelled"
                            ? "error.main"
                            : event.action === "payment_updated"
                            ? "info.main"
                            : "primary.main",
                      }}
                    />

                    {/* Contenido del evento */}
                    <Paper sx={{ p: 2 }} variant="outlined">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" fontWeight="medium">
                          {event.actionLabel}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(event.timestamp)}
                        </Typography>
                      </Box>

                      {event.user && (
                        <Box
                          sx={{
                            mb: 1,
                            p: 1.5,
                            bgcolor: "grey.50",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.200",
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="medium"
                            display="block"
                            sx={{ mb: 0.5 }}
                          >
                            👤 Responsable:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {event.user.fullName ||
                              event.user.name ||
                              "Sin nombre"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            📧 {event.user.email || "Sin email"}
                          </Typography>
                          {event.user.cellphone && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              📱 {event.user.cellphone}
                            </Typography>
                          )}
                          {event.user.id && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              🆔 ID: {event.user.id}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {event.details &&
                        Object.keys(event.details).length > 0 && (
                          <Box
                            sx={{
                              mt: 1,
                              p: 1,
                              bgcolor: "action.hover",
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                              display="block"
                              sx={{ mb: 0.5 }}
                            >
                              📋 Detalles del cambio:
                            </Typography>
                            {Object.entries(event.details).map(
                              ([key, value]) => (
                                <Typography
                                  key={key}
                                  variant="caption"
                                  display="block"
                                >
                                  <strong>{key}:</strong>{" "}
                                  {typeof value === "object"
                                    ? JSON.stringify(value)
                                    : value?.toString() || "N/A"}
                                </Typography>
                              )
                            )}
                          </Box>
                        )}
                    </Paper>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {!loadingAudit && !auditHistory && (
            <Alert severity="warning">
              No se pudo cargar el historial de auditoría
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setAuditHistoryDialog({
                open: false,
                saleId: null,
                saleName: "",
              })
            }
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🆕 NUEVO: Modal de Edición de Venta */}
      <DirectSaleModal
        open={editSaleDialog.open}
        onClose={() => setEditSaleDialog({ open: false, sale: null })}
        product={editSaleDialog.sale?.lot}
        saleToEdit={editSaleDialog.sale}
        onSaleCreated={handleSaleUpdated}
      />
    </Box>
  );
};

export default DirectSalesList;
